import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { ApplicationConfig, APP_INITIALIZER } from '@angular/core';
import { LuxonDateAdapter } from '@angular/material-luxon-adapter';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { PreloadAllModules, provideRouter, RouteReuseStrategy, withInMemoryScrolling, withPreloading } from '@angular/router';
import { provideFuse } from '@fuse';
import { appRoutes } from 'app/app.routes';
import { provideAuth } from 'app/core/auth/auth.provider';
import { provideIcons } from 'app/core/icons/icons.provider';
import { provideNgxTranslate } from './core/ngx-translate/translate.provider';
import { mockApiServices } from 'app/mock-api';

import { JwtInterceptor } from './core/http/jwt.interceptor';
import { JwtErrorInterceptor } from './core/http/jwt-error.interceptor';
import { errorHandlerInterceptor } from './core/http/error-handler.interceptor';

import { ENVIRONMENT_INITIALIZER, EnvironmentProviders, importProvidersFrom, inject, Provider } from '@angular/core';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { TranslatedPaginatorIntl } from './core/i18n/translated-paginator-intl';

import { AppConfigService } from './core/config/app-config.service';
import { RecreateOnParamChangeStrategy } from './core/router/recreate-on-param-change.strategy';

export const appConfig: ApplicationConfig = {
  providers: [
    // ── Runtime config: load /_config/config.json before anything else ────────
    {
      provide: APP_INITIALIZER,
      useFactory: (cfg: AppConfigService) => () => cfg.load(),
      deps: [AppConfigService],
      multi: true,
    },

    provideAnimations(),
    provideHttpClient(
      withInterceptors([
        JwtInterceptor,        // 1. Adds JWT token to requests
        JwtErrorInterceptor,   // 2. Handles 401 errors, refreshes token
        errorHandlerInterceptor // 3. Handles other errors, shows snackbar
      ])
    ),

    importProvidersFrom(MatSnackBarModule),

    provideRouter(
      appRoutes,
      withPreloading(PreloadAllModules),
      withInMemoryScrolling({ scrollPositionRestoration: 'enabled' })
      // withDebugTracing()
    ),
    { provide: RouteReuseStrategy, useClass: RecreateOnParamChangeStrategy },

    // Material Date Adapter
    {
      provide: DateAdapter,
      useClass: LuxonDateAdapter,
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: {
        parse: {
          dateInput: 'D',
        },
        display: {
          dateInput: 'DDD',
          monthYearLabel: 'LLL yyyy',
          dateA11yLabel: 'DD',
          monthYearA11yLabel: 'LLLL yyyy',
        },
      },
    },

    // Transloco Config
    //provideTransloco(),
    provideNgxTranslate(),
    { provide: MatPaginatorIntl, useClass: TranslatedPaginatorIntl },

    // Fuse
    provideAuth(),
    provideIcons(),
    provideFuse({
      mockApi: {
        delay: 0,
        services: mockApiServices,
      },
      fuse: {
        layout: 'compact',
        scheme: 'light',
        screens: {
          xs: '350px',
          sm: '600px',
          md: '960px',
          lg: '1285px',
          xl: '1440px',
        },
        theme: 'theme-default',
        themes: [
          {
            id: 'theme-default',
            name: 'Default',
          },
          {
            id: 'theme-brand',
            name: 'Brand',
          },
          {
            id: 'theme-teal',
            name: 'Teal',
          },
          {
            id: 'theme-rose',
            name: 'Rose',
          },
          {
            id: 'theme-purple',
            name: 'Purple',
          },
          {
            id: 'theme-amber',
            name: 'Amber',
          },
        ],
      },
    }),
  ],
};
